import { Link } from 'react-router-dom';
import { ENGINE_SNIPPETS, type EngineSnippet } from '../generated/engineSnippets';
import { ArchitectureDiagram } from './ArchitectureDiagram';

/** Look up a vetted engine snippet by its key. Returns undefined if
 *  the build-time extraction didn't include it (defensive — the page
 *  renders fine without the code block). */
function snippet(key: string): EngineSnippet | undefined {
  return ENGINE_SNIPPETS.find(s => s.key === key);
}

/**
 * Admin → Recon Logic.
 *
 * Plain-English documentation of how the recon engine decides every
 * status it emits. Intended audience: operators (Sandeep), auditors,
 * compliance reviewers, future devs reading this without going to
 * code.
 *
 * Source of truth for the engine itself is
 *   backend/src/modules/module-1-portfolio-dp-recon/reconcile/txnMatchEngine.ts
 * Any change to that file MUST update this page, and vice versa.
 * Quick checklist when changing engine logic:
 *
 *   1. Update signedQty / dpSignedQty / classification in the engine
 *   2. Update mathTrace.ts (frontend mirror)
 *   3. Update signedPfQty in api/router.ts (xlsx export helper)
 *   4. Update the relevant section of THIS page
 *   5. Add or update a pin test in txnMatchEngine.test.ts
 *
 * The page is intentionally NOT generated from the engine code. The
 * narrative format is hard to autogenerate well, and the cost of
 * keeping it manually in sync is low compared to the cost of a
 * Sandeep-style "wait, how did you get this number?" question going
 * unanswered.
 */
export function ReconLogicPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-strong)' }}>
          Reconciliation Logic
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-200)' }}>
          How the engine decides every recon status. Use this page to understand,
          explain, or audit a result. Last updated alongside the engine on May 27, 2026.
        </p>
        <p className="text-sm mt-2 px-3 py-2 rounded-md"
           style={{
             color: 'var(--text-200)',
             background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
             border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
           }}>
          For audit transparency, each rule below is followed by the
          actual source code that implements it — shown verbatim with
          its real file path and line numbers, extracted directly from
          the engine at build time. The code you see here is the code
          that runs.
        </p>
      </header>

      {/* ─── Overview ─────────────────────────────────────────────── */}
      <Section title="What the engine does">
        <P>
          The engine compares portfolio transactions (from the broker's
          trade book) against DP transactions (from the depository
          statement). It produces one row per ISIN, classifying each
          as MATCH, QTY_MISMATCH, SLB_OUTSTANDING, ONLY_IN_PORTFOLIO,
          or ONLY_IN_DP.
        </P>
        <P>
          The recon operates in <Strong>net-balance mode</Strong>:
          instead of matching individual transactions to each other,
          the engine sums quantities on each side and compares the
          totals. This handles partial fills, settlement timing
          differences, and broker-specific transaction numbering
          without requiring a one-to-one transaction match.
        </P>
      </Section>

      {/* ─── Architecture ─────────────────────────────────────────── */}
      <Section title="Architecture">
        <P>
          The platform is built entirely on industry-standard
          open-source technologies. The diagram below shows the
          layers and the frameworks at each one.
        </P>
        <ArchitectureDiagram />
      </Section>

      {/* ─── Inputs ────────────────────────────────────────────────── */}
      <Section title="Inputs">
        <P>The engine consumes three things:</P>
        <ul className="ml-5 space-y-2 text-sm list-disc" style={{ color: 'var(--text-200)' }}>
          <li>
            <Code>portfolio_txns</Code> — every transaction from the
            portfolio Excel. Each has an ISIN, date, type (BUY / SELL /
            BONUS / etc), and a quantity.
          </li>
          <li>
            <Code>dp_holdings</Code> — every ISIN section from the DP
            statement(s), with its opening balance, closing balance, and
            the list of transactions within the statement period.
          </li>
          <li>
            <Code>statement_from</Code> and <Code>statement_to</Code> —
            the DP statement's reporting period, when known. Optional;
            when both are null, the engine sums every portfolio txn
            regardless of date.
          </li>
        </ul>
      </Section>

      {/* ─── Portfolio net ────────────────────────────────────────── */}
      <Section title="Step 1: Portfolio net">
        <P>
          For each portfolio transaction, the engine computes a signed
          quantity. The sign depends on the transaction type:
        </P>

        <ClassTable
          title="Buy-side (+qty)"
          rows={[
            ['BUY', 'Regular purchase via exchange'],
            ['BUYCR', 'Carry-over buy from previous period'],
            ['BONUS', 'Bonus shares issued by the company'],
            ['SPLIT BUY', 'Shares received from a stock split'],
            ['DEMERGERIN', 'Shares received in a demerger'],
            ['DEMERGERBUY', 'Demerger buy-side entry'],
            ['TRANSFERIN / INTRANSFER', 'Off-market transfer arriving from another DP'],
            ['MERGER BUY', 'New company shares from an M&A merger'],
          ]}
        />

        <ClassTable
          title="Sell-side (-qty)"
          rows={[
            ['SELL', 'Regular sale via exchange'],
            ['SELLCR', 'Carry-over sell from previous period'],
            ['SPLIT SELL', 'Shares cancelled in a stock split'],
            ['DEMERGERSELL', 'Demerger sell-side entry'],
            ['TRANSFEROUT / OUTTRANSFER', 'Off-market transfer leaving this DP'],
            ['MERGER SELL', 'Old company shares extinguished in a merger'],
          ]}
        />

        <ClassTable
          title="Cash event (qty = 0, doesn't affect net)"
          rows={[
            ['INTERIM DIVIDEND', 'Interim cash dividend'],
            ['FINAL DIVIDEND', 'Final cash dividend'],
            ['SPECIAL DIVIDEND', 'Special cash dividend'],
            ['INTERIM 1-4 DIVIDEND', 'Numbered interim dividend variants'],
            ['DEMERGERAMT', 'Cash component of a demerger'],
            ['BONUSAMT', 'Cash component of a bonus issue'],
          ]}
        />

        <P>
          <Strong>Unknown types contribute 0 to net</Strong> — a defensive
          default. If a portfolio Excel introduces a new transaction
          type the engine doesn't recognize, the recon won't crash;
          it'll just ignore the contribution. A reviewer would see the
          unmatched-by-net value as a QTY_MISMATCH and investigate.
          When you discover one, add it to the table above (and update
          the engine + tests).
        </P>

        <P>
          <Code>portfolio_net</Code> for an ISIN is the sum of signed
          quantities across all its in-window transactions.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="portfolio_net" />
      </Section>

      {/* ─── DP net ────────────────────────────────────────────────── */}
      <Section title="Step 2: DP net">
        <P>
          For each DP transaction, the signed quantity is
          {' '}<Code>credit - debit</Code>. Credits increase the
          holding; debits decrease it.
        </P>
        <P>
          Before summing, the engine deduplicates DP transactions
          using a composite key
          {' '}(<Code>txn_no | date | description | credit | debit</Code>).
          This is a belt-and-braces guard: if the upstream extractor
          ever returns the same transaction twice (e.g. overlapping
          PDF chunks, accidental double-PDF upload), the engine will
          count it once. A logged warning is the only sign this
          happened.
        </P>
        <P>
          <Code>dp_net</Code> is the sum of <Code>credit - debit</Code> across
          the deduplicated transactions for that ISIN.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="dp_net" />
        <CodeBlock snippetKey="dp_dedup" />
      </Section>

      {/* ─── Period window ────────────────────────────────────────── */}
      <Section title="Step 3: Statement period filter">
        <P>
          DP statements only contain transactions within the period
          they cover. If the portfolio Excel covers a longer window
          (a full year of history, say), comparing the full portfolio
          to a one-month DP statement would produce false mismatches
          for every position bought before the DP period.
        </P>
        <P>
          When both <Code>statement_from</Code> and{' '}
          <Code>statement_to</Code> are populated, the engine filters
          portfolio transactions to that window before summing.
          Out-of-window transactions are preserved in the audit trail
          with status <Code>OUTSIDE_WINDOW</Code> (visible on the
          verify page, greyed) but don't contribute to{' '}
          <Code>portfolio_net</Code>.
        </P>
        <P>
          When either value is null, the engine falls back to summing
          every portfolio transaction. This is backward-compatible
          with DP files whose extractor didn't capture the period
          metadata.
        </P>
        <P>
          <Strong>Edge case:</Strong> an ISIN whose only portfolio
          activity is pre-window shows as MATCH (both nets = 0), not
          ONLY_IN_PORTFOLIO. The operator's intent in this case is
          "I held this stock but didn't trade it during the period" —
          not "this is missing from the DP". The verify page surfaces
          the out-of-window count on the rollup so reviewers can see
          why.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="period_filter" />
      </Section>

      {/* ─── Classification ─────────────────────────────────────────── */}
      <Section title="Step 4: Status decision">
        <P>
          With <Code>portfolio_net</Code> and <Code>dp_net</Code>{' '}
          computed, the engine assigns a status to each ISIN. The
          decision tree, in order:
        </P>

        <Tree>
          <li><Strong>If</Strong> ISIN exists in portfolio but NOT in DP
            <ul><li>→ <StatusPill>ONLY_IN_PORTFOLIO</StatusPill></li></ul>
          </li>
          <li><Strong>Else if</Strong> ISIN exists in DP but NOT in portfolio
            <ul><li>→ <StatusPill>ONLY_IN_DP</StatusPill></li></ul>
          </li>
          <li><Strong>Else if</Strong> |portfolio_net − dp_net| ≤ tolerance (0.001 shares)
            <ul><li>→ <StatusPill>MATCH</StatusPill></li></ul>
          </li>
          <li><Strong>Else if</Strong> SLB outstanding explains the gap
            (see next section)
            <ul><li>→ <StatusPill>SLB_OUTSTANDING</StatusPill></li></ul>
          </li>
          <li><Strong>Else</Strong>
            <ul><li>→ <StatusPill tone="warn">QTY_MISMATCH</StatusPill></li></ul>
          </li>
        </Tree>

        <P>
          <Code>difference</Code> on the rollup is{' '}
          <Code>portfolio_net − dp_net</Code>. A positive difference
          means the portfolio shows more shares than the DP; a
          negative one means the DP shows more.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="status_decision" />
      </Section>

      {/* ─── SLB detection ────────────────────────────────────────── */}
      <Section title="SLB outstanding (special case)">
        <P>
          When SEBI's Securities Lending & Borrowing (SLB) shares are
          on loan, the portfolio still beneficially owns them but
          they're physically out of the DP. The engine detects this
          pattern explicitly so reviewers don't waste time investigating
          a gap that's expected.
        </P>
        <P>
          SLB outstanding is computed from DP transactions whose
          description starts with "SLB FIRST LEG" (debit; share went
          out on loan) and "SLB SECOND LEG" (credit; share came back).
          Open positions = sum of FIRST LEG debits minus matching
          SECOND LEG credits as of <Code>statement_to</Code>.
        </P>
        <P>
          If <Code>dp_net + slb_outstanding == portfolio_net</Code>{' '}
          (within tolerance), the engine emits SLB_OUTSTANDING instead
          of QTY_MISMATCH. The verify page shows the SLB calculation
          so the reviewer can confirm.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="slb_classify" />
        <CodeBlock snippetKey="slb_compute" />
      </Section>

      {/* ─── Tolerance ────────────────────────────────────────────── */}
      <Section title="Tolerance">
        <P>
          The engine treats any difference within{' '}
          <Code>QTY_TOLERANCE = 0.001 shares</Code> as MATCH. This
          absorbs floating-point precision and the rare fractional-
          share quirk in mutual-fund-like instruments. The threshold
          is intentionally tight: real mismatches are almost always
          whole shares.
        </P>

        <CodeSectionHeading />
        <CodeBlock snippetKey="tolerance" />
      </Section>

      {/* ─── What it doesn't do ─────────────────────────────────────── */}
      <Section title="What the engine does NOT do">
        <ul className="ml-5 space-y-2 text-sm list-disc" style={{ color: 'var(--text-200)' }}>
          <li>
            It does not match individual transactions to each other.
            Net-balance mode compares totals, not pairs. If you need
            to know which specific portfolio BUY corresponds to which
            specific DP credit, that information is in the audit
            trail (recon_txn_pair) but the engine itself doesn't pair
            them.
          </li>
          <li>
            It does not validate prices or trade values. Quantity is
            the only reconciled field. Cash totals (dividends, charges)
            are tracked for reporting but not reconciled.
          </li>
          <li>
            It does not project the closing balance. The engine compares
            net changes during the period; <Code>dp_closing_balance</Code> is
            stored for reference but doesn't drive the status decision.
          </li>
          <li>
            It does not currently support multi-account consolidated
            DP files. One DP file = one DP statement.
          </li>
          <li>
            It does not flag corporate actions automatically. Splits,
            bonuses, and demergers are recognized for net-quantity
            purposes but a separate reviewer step is required to
            confirm the corporate action itself.
          </li>
        </ul>
      </Section>

      {/* ─── Cross-references ─────────────────────────────────────── */}
      <Section title="See also">
        <ul className="ml-5 space-y-1 text-sm list-disc" style={{ color: 'var(--text-200)' }}>
          <li>
            <Link to="/admin/corporate-actions" className="underline"
                  style={{ color: 'var(--accent)' }}>
              Corporate Actions
            </Link>{' '}— ratios used to map pre/post split-bonus quantities.
          </li>
          <li>
            <Link to="/admin/prompts" className="underline"
                  style={{ color: 'var(--accent)' }}>
              Prompts
            </Link>{' '}— the LLM extractor prompts (only used for PDF DP files;
            CSV/Excel files bypass this).
          </li>
          <li>
            On any reconciliation result, the math icon (Σ) next to
            each row opens a step-by-step trace showing exactly which
            transactions contributed to that row's status.
          </li>
        </ul>
      </Section>
    </div>
  );
}

/* ─── Small presentational helpers ──────────────────────────────────── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-base font-semibold mb-3 pb-2"
          style={{
            color: 'var(--text-strong)',
            borderBottom: '1px solid var(--glass-border)',
          }}>
        {title}
      </h2>
      <div className="space-y-3">
        {children}
      </div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-200)' }}>
      {children}
    </p>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return (
    <strong style={{ color: 'var(--text-strong)', fontWeight: 600 }}>
      {children}
    </strong>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded text-xs font-mono"
          style={{
            background: 'color-mix(in srgb, var(--text-200) 12%, transparent)',
            color: 'var(--text-strong)',
          }}>
      {children}
    </code>
  );
}

/**
 * Small "Implementation" label that precedes a CodeBlock, making clear
 * the following excerpt is the actual code that runs (not a paraphrase).
 */
function CodeSectionHeading() {
  return (
    <div className="mt-4 mb-1 text-[11px] uppercase tracking-[0.15em] font-semibold"
         style={{ color: 'var(--text-200)' }}>
      Implementation — verbatim from source
    </div>
  );
}

/**
 * Renders a vetted engine source excerpt with real filename + line
 * numbers, so a client auditor can verify the displayed code against
 * the repository. The line numbers come from the build-time extraction
 * (engineSnippets.ts) and reflect the actual position in the source
 * file. Read-only; no runtime filesystem access.
 *
 * Looked up by key — if the snippet isn't present (extraction skipped),
 * renders nothing rather than breaking the page.
 */
function CodeBlock({ snippetKey }: { snippetKey: string }) {
  const s = snippet(snippetKey);
  if (!s) return null;
  const codeLines = s.code.split('\n');
  return (
    <div className="my-4 rounded-md overflow-hidden border"
         style={{ borderColor: 'var(--glass-border)' }}>
      <div className="px-3 py-2 flex items-center justify-between text-[11px] font-mono"
           style={{ background: 'var(--bg-elevated)', color: 'var(--text-200)' }}>
        <span style={{ color: 'var(--text-strong)' }}>{s.file}</span>
        <span>lines {s.startLine}–{s.endLine}</span>
      </div>
      <div className="overflow-x-auto" style={{ background: 'var(--bg-surface)' }}>
        <table className="text-xs font-mono" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {codeLines.map((line, i) => {
              const lineNo = s.startLine + i;
              return (
                <tr key={lineNo}>
                  <td className="px-3 py-0.5 text-right select-none align-top"
                      style={{
                        color: 'color-mix(in srgb, var(--text-200) 60%, transparent)',
                        background: 'var(--bg-elevated)',
                        borderRight: '1px solid var(--glass-border)',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        width: '1%',
                      }}>
                    {lineNo}
                  </td>
                  <td className="px-3 py-0.5 align-top"
                      style={{
                        color: 'var(--text-strong)',
                        whiteSpace: 'pre',
                      }}>
                    {line || ' '}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClassTable({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="my-3 rounded-md overflow-hidden border"
         style={{ borderColor: 'var(--glass-border)' }}>
      <div className="px-3 py-2 text-xs font-semibold"
           style={{
             background: 'var(--bg-elevated)',
             color: 'var(--text-strong)',
           }}>
        {title}
      </div>
      <table className="w-full text-xs">
        <tbody>
          {rows.map(([type, desc]) => (
            <tr key={type} className="border-t"
                style={{ borderColor: 'var(--glass-border)' }}>
              <td className="px-3 py-1.5 font-mono whitespace-nowrap"
                  style={{ color: 'var(--text-strong)', width: '34%' }}>
                {type}
              </td>
              <td className="px-3 py-1.5"
                  style={{ color: 'var(--text-200)' }}>
                {desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tree({ children }: { children: React.ReactNode }) {
  return (
    <ul className="ml-5 text-sm space-y-2 list-none"
        style={{ color: 'var(--text-200)' }}>
      {children}
    </ul>
  );
}

function StatusPill({ children, tone }: { children: React.ReactNode; tone?: 'warn' }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-mono"
          style={{
            background: tone === 'warn'
              ? 'color-mix(in srgb, var(--status-warn) 18%, transparent)'
              : 'color-mix(in srgb, var(--status-info) 18%, transparent)',
            color: tone === 'warn' ? 'var(--status-warn)' : 'var(--status-info)',
            border: tone === 'warn'
              ? '1px solid color-mix(in srgb, var(--status-warn) 40%, transparent)'
              : '1px solid color-mix(in srgb, var(--status-info) 40%, transparent)',
          }}>
      {children}
    </span>
  );
}
